/**
 * CardOverview Organism Component
 *
 * Main dispatcher component that renders the appropriate card layout variation
 * based on the provided layout configuration. Supports three variations:
 * - Vertical layout: Featured cards with vertical orientation
 * - Horizontal layout: Card image on left, content on right
 * - Default/BLP layout: Standard vertical card with overlapping image
 *
 * @module organisms/CardOverview
 */

// External library imports
import { FC, useMemo, memo } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// Internal library imports - ordered by proximity to current file
import { CardOverviewProps } from './CardOverview.types';
import { CardOverviewHorizontal } from '../../molecules/CardOverviewHorizontal';
import { CardOverviewVertical } from '../../molecules/CardOverviewVertical';
import { CARD_LAYOUT_TYPE } from '../../modules/constants/constants';

// AEM component configuration
export const CardOverviewConfig = {
  emptyLabel: 'CardOverview',
  isEmpty: () => true,
  resourceType: `/components/content/cardoverview`,
};

/**
 * CardOverviewComp - Smart Dispatcher Component
 *
 * Performance optimizations:
 * - Memoized component with React.memo
 * - useMemo for layout type determination
 * - Props passed through without modification for maximum efficiency
 *
 * Determines which card layout variation to render based on the layout configuration.
 * Acts as a smart dispatcher that delegates rendering to specialized components:
 * - CardOverviewHorizontal: For horizontal layout variation
 * - CardOverviewVertical: For vertical and default/BLP layout variations
 *
 * @param props - Component properties
 * @returns JSX element representing the appropriate card layout
 */
export const CardOverviewComp: FC<CardOverviewProps> = memo<CardOverviewProps>(props => {
  // Extract variation to determine which component to render
  const { variation = CARD_LAYOUT_TYPE.DEFAULT } = props;

  // Memoize layout type determination for performance
  const isHorizontalLayout = useMemo(() => variation === CARD_LAYOUT_TYPE.HORIZONTAL, [variation]);

  // Delegate rendering to appropriate layout variation component
  if (isHorizontalLayout) {
    return <CardOverviewHorizontal {...props} />;
  }

  // Default to vertical layout for both vertical and BLP variations
  return <CardOverviewVertical {...props} />;
});

CardOverviewComp.displayName = 'CardOverviewComp';
/**
 * CardOverview - AEM Editable Component Wrapper
 *
 * Wraps the CardOverviewComp in an AEM EditableComponent for authoring support.
 * This allows the component to be edited in AEM's authoring interface.
 *
 * @param props - Component properties
 * @returns AEM-editable card overview component
 */
export const CardOverview: FC<CardOverviewProps> = props => {
  return (
    <EditableComponent config={CardOverviewConfig} {...props}>
      <CardOverviewComp {...props} />
    </EditableComponent>
  );
};
